import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import DashboardSidebar from '@/components/DashboardSidebar';
import DashboardTopNav from '@/components/DashboardTopNav';
import ProgressSteps from '@/components/ProgressSteps';
import EmailVerificationPrompt from '@/components/EmailVerificationPrompt';
import { PersonalInfoService, DocumentService, ApplicationFormService, AppointmentService, UserServiceService } from '@/integrations/supabase/services';
import { FileCheck, Clock, Calendar, AlertCircle, CheckCircle2, User } from 'lucide-react';
import '@/styles/animations.css';

const Dashboard = () => {
  const { user, isEmailVerified } = useAuth();
  const [completionStatus, setCompletionStatus] = useState({
    serviceSelection: false,
    personalInfo: false,
    documents: false,
    applicationForm: false,
    appointment: false
  });
  const [recentActivities, setRecentActivities] = useState([
    {
      action: "Account created",
      description: "Welcome to Sigma Immigration",
      timestamp: "Today, 10:15 AM",
      type: "account",
      icon: User
    }
  ]);

  // Show email verification prompt if user is not verified
  if (user && !isEmailVerified) {
    return <EmailVerificationPrompt />;
  }

  useEffect(() => {
    if (user) {
      checkCompletionStatus();
    }
  }, [user]);

  const checkCompletionStatus = async () => {
    if (!user) return;

    try {
      // Check service selection
      const userServices = await UserServiceService.getByUserId(user.uid);

      // Check personal info
      const personalInfoList = await PersonalInfoService.getByUserId(user.uid);
      const personalInfo = personalInfoList.length > 0 ? personalInfoList[0] : null;

      // Check documents
      const documents = await DocumentService.getByUserId(user.uid);

      // Check application form
      const applicationForms = await ApplicationFormService.getByUserId(user.uid);
      const applicationForm = applicationForms.length > 0 ? applicationForms[0] : null;

      // Check appointments
      const appointments = await AppointmentService.getByUserId(user.uid);

      const status = {
        serviceSelection: userServices && userServices.length > 0,
        personalInfo: !!personalInfo,
        documents: documents && documents.length >= 7,
        applicationForm: !!applicationForm,
        appointment: appointments && appointments.length > 0
      };

      setCompletionStatus(status);

      // Update recent activities based on completion
      const activities = [
        {
          action: "Account created",
          description: "Welcome to Sigma Immigration",
          timestamp: "Today, 10:15 AM",
          type: "account",
          icon: User
        }
      ];

      if (status.serviceSelection) {
        activities.unshift({
          action: "Services selected",
          description: "Service preferences configured",
          timestamp: "Today, 10:45 AM",
          type: "service",
          icon: CheckCircle2
        });
      }

      if (status.personalInfo) {
        activities.unshift({
          action: "Personal information completed",
          description: "Profile details submitted",
          timestamp: "Today, 11:30 AM",
          type: "completion",
          icon: CheckCircle2
        });
      }

      if (status.documents) {
        activities.unshift({
          action: "Documents uploaded",
          description: "Required documents submitted",
          timestamp: "Today, 12:15 PM",
          type: "upload",
          icon: FileCheck
        });
      }

      if (status.applicationForm) {
        activities.unshift({
          action: "Application form completed",
          description: "All form fields submitted",
          timestamp: "Today, 1:00 PM",
          type: "form",
          icon: CheckCircle2
        });
      }

      if (status.appointment) {
        activities.unshift({
          action: "Appointment scheduled",
          description: "Meeting time confirmed",
          timestamp: "Today, 1:30 PM",
          type: "appointment",
          icon: Calendar
        });
      }

      setRecentActivities(activities);
    } catch (error) {
      console.error('Error checking completion status:', error);
    }
  };

  const steps = [
    {
      id: 1,
      title: "Service Selection",
      description: "Choose your services",
      completed: completionStatus.serviceSelection,
      current: !completionStatus.serviceSelection
    },
    {
      id: 2,
      title: "Package Selection",
      description: "Choose your package",
      completed: true,
      current: false
    },
    {
      id: 3,
      title: "Personal Information",
      description: "Enter your details",
      completed: completionStatus.personalInfo,
      current: completionStatus.serviceSelection && !completionStatus.personalInfo
    },
    {
      id: 4,
      title: "Documents Upload",
      description: "Upload required documents",
      completed: completionStatus.documents,
      current: completionStatus.personalInfo && !completionStatus.documents
    },
    {
      id: 5,
      title: "Application Form",
      description: "Fill application details",
      completed: completionStatus.applicationForm,
      current: completionStatus.documents && !completionStatus.applicationForm
    },
    {
      id: 6,
      title: "Review & Submit",
      description: "Review application",
      completed: completionStatus.personalInfo && completionStatus.documents && completionStatus.applicationForm,
      current: completionStatus.applicationForm && !(completionStatus.personalInfo && completionStatus.documents && completionStatus.applicationForm)
    },
    {
      id: 7,
      title: "Schedule Appointment",
      description: "Book your appointment",
      completed: completionStatus.appointment,
      current: (completionStatus.personalInfo && completionStatus.documents && completionStatus.applicationForm) && !completionStatus.appointment
    }
  ];

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <DashboardSidebar />
        <SidebarInset className="flex-1 flex flex-col">
          <DashboardTopNav pageTitle="Dashboard" />
          <main className="flex-1 p-4 sm:p-8 mt-[3.5rem] sm:mt-[4.2rem] overflow-auto">
            {/* Progress Section */}
            <div className="mb-8 animate-slide-in-bottom" style={{ animationDelay: '200ms' }}>
              <Card className="border-border backdrop-blur-sm">
                <CardHeader className="p-6">
                  <CardTitle className="text-2xl font-alumni">Application Progress</CardTitle>
                  <CardDescription className="font-hind">
                    Complete all steps to submit your application
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <ProgressSteps steps={steps} />
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <Card className="border-border backdrop-blur-sm animate-slide-in-left" style={{ animationDelay: '300ms' }}>
                <CardHeader className="p-6">
                  <CardTitle className="text-2xl font-alumni">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {recentActivities.map((activity, index) => {
                      const IconComponent = activity.icon;
                      return (
                        <div 
                          key={index} 
                          className="flex items-start space-x-4 group animate-slide-in-left"
                          style={{ animationDelay: `${400 + index * 100}ms` }}
                        >
                          <div className="bg-primary/10 p-2 rounded-full group-hover:bg-primary/20 transition-colors">
                            <IconComponent className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 space-y-1">
                            <p className="text-sm font-medium text-foreground">{activity.action}</p>
                            <p className="text-sm text-muted-foreground font-hind">{activity.description}</p>
                            <p className="text-xs text-muted-foreground/60 font-hind">{activity.timestamp}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Status Overview */}
              <Card className="border-border backdrop-blur-sm animate-slide-in-right" style={{ animationDelay: '300ms' }}>
                <CardHeader className="p-6">
                  <CardTitle className="text-2xl font-alumni">Application Status</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(completionStatus).map(([key, completed], index) => {
                        const title = key.replace(/([A-Z])/g, ' $1').toLowerCase().replace(/^\w/, c => c.toUpperCase());
                        return (
                          <div 
                            key={key}
                            className="p-4 rounded-lg bg-primary/5 backdrop-blur-sm animate-slide-in-right group hover:bg-primary/10 transition-all duration-300"
                            style={{ animationDelay: `${400 + index * 100}ms` }}
                          >
                            <div className="flex items-center space-x-3">
                              {completed ? (
                                <CheckCircle2 className="h-5 w-5 text-primary" />
                              ) : (
                                <Clock className="h-5 w-5 text-muted-foreground" />
                              )}
                              <div>
                                <p className="text-sm font-medium text-foreground">{title}</p>
                                <p className="text-xs text-muted-foreground font-hind">
                                  {completed ? 'Completed' : 'Pending'}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Tips Section */}
            <Card className="border-border backdrop-blur-sm mt-6 animate-slide-in-bottom" style={{ animationDelay: '400ms' }}>
              <CardHeader className="p-6">
                <CardTitle className="text-2xl font-alumni">Quick Tips</CardTitle>
                <CardDescription className="font-hind">Helpful information for a smooth application process</CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-primary/5 backdrop-blur-sm group hover:bg-primary/10 transition-all duration-300">
                    <div className="flex items-start space-x-3">
                      <FileCheck className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Document Preparation</p>
                        <p className="text-sm text-muted-foreground font-hind">Ensure all documents are scanned in high quality and in PDF format.</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-primary/5 backdrop-blur-sm group hover:bg-primary/10 transition-all duration-300">
                    <div className="flex items-start space-x-3">
                      <Calendar className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Appointment Tips</p>
                        <p className="text-sm text-muted-foreground font-hind">Book your appointment early and arrive 15 minutes before the scheduled time.</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg bg-primary/5 backdrop-blur-sm group hover:bg-primary/10 transition-all duration-300">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-primary mt-1" />
                      <div>
                        <p className="text-sm font-medium text-foreground mb-1">Important Notice</p>
                        <p className="text-sm text-muted-foreground font-hind">Keep your contact information up to date for important notifications.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default Dashboard;
